import { atom, useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";

/**
 * Makes the accordion panes addressable from anywhere. `ReflexAccordion` names panes by index and
 * exposes toggling only *inside* a pane, and it drops any child that is not a
 * `ReflexAccordionItem` — so each pane subscribes and toggles itself instead.
 */
export type SidebarPaneId = "published" | "drafts" | "data-sources" | "exports";

const pendingOpenPaneAtom = atom<SidebarPaneId | null>(null);

/** Callable before the sidebar mounts. */
export function useOpenSidebarPane() {
  const setPending = useSetAtom(pendingOpenPaneAtom);
  return useCallback(
    (paneId: SidebarPaneId) => setPending(paneId),
    [setPending],
  );
}

/** Takes `toggle` rather than reading the accordion context, so it carries no hidden dependency. */
export function useRespondToPaneOpenRequests(
  paneId: SidebarPaneId,
  toggle: (shouldOpen?: boolean) => void,
) {
  const [pending, setPending] = useAtom(pendingOpenPaneAtom);

  useEffect(() => {
    if (pending !== paneId) return;
    toggle(true);
    // Consumed, so a later render cannot re-open a pane the user collapsed.
    setPending(null);
  }, [pending, paneId, toggle, setPending]);
}
